import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {DialogSubmitFooter} from '@/components/dialog-submit-footer'
import {AmountIvaFields, validateAmountIva} from '@/components/cartera/amount-iva-fields'
import {useSetPriceOverride, useToggleSst, type PriceResolution} from '@/hooks/inci/cartera/use-cartera'
import {translateApiError} from '@/lib/errors'
import {formatCopFull} from '@/lib/money'

/** Un solo submit encadena el override del ítem `sst` y su encendido en la suscripción. */
export function EmpresaSstEncenderDialog({
  open,
  onOpenChange,
  businessId,
  subscriptionId,
  segmentKey,
  billingCycle,
  currentSstPrice,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  subscriptionId: string
  segmentKey: string
  billingCycle: 'mensual' | 'anual'
  currentSstPrice: PriceResolution | null | undefined
}) {
  const {t} = useTranslation()
  const setOverride = useSetPriceOverride()
  const toggleSst = useToggleSst()
  const [amount, setAmount] = useState('')
  const [iva, setIva] = useState('19')
  const [error, setError] = useState<string | null>(null)

  function handleOpenChange(next: boolean) {
    if (!next) {
      setAmount('')
      setIva('19')
      setError(null)
      setOverride.reset()
      toggleSst.reset()
    }
    onOpenChange(next)
  }

  function blurValidate() {
    if (!amount.trim() || !iva.trim()) {
      setError(null)
      return
    }
    const validated = validateAmountIva(t, amount, iva)
    setError('error' in validated ? validated.error : null)
  }

  function submit() {
    const validated = validateAmountIva(t, amount, iva)
    if ('error' in validated) {
      setError(validated.error)
      return
    }
    setError(null)
    setOverride.mutate(
      {
        item_kind: 'sst',
        segment_key: segmentKey,
        billing_cycle: billingCycle,
        amount_cop: Math.round(validated.amountNum),
        iva_pct: Math.round(validated.ivaNum),
        business_id: businessId,
      },
      {
        onSuccess: () => {
          toggleSst.mutate(
            {subscriptionId, incluir: true},
            {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
          )
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  const pending = setOverride.isPending || toggleSst.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="empresa-sst-encender-dialog">
        <DialogHeader>
          <DialogTitle>{t('cartera.empresaPanel.sstDialogTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('cartera.empresaPanel.sstDialogHint')}</p>
        {currentSstPrice && (
          <p className="text-sm text-muted-foreground" data-testid="empresa-sst-precio-vigente">
            {t('cartera.empresaPanel.sstDialogVigente', {amount: formatCopFull(currentSstPrice.amount_cop)})}
          </p>
        )}
        <div className="space-y-3">
          <AmountIvaFields
            idPrefix="empresa-sst"
            amount={amount}
            iva={iva}
            onAmountChange={(value) => {
              setAmount(value)
              setError(null)
            }}
            onIvaChange={(value) => {
              setIva(value)
              setError(null)
            }}
            onBlur={blurValidate}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogSubmitFooter
          onCancel={() => handleOpenChange(false)}
          onSubmit={submit}
          submitLabel={t('cartera.empresaPanel.sstDialogSubmit')}
          pending={pending}
          testId="empresa-sst-encender-submit"
        />
      </DialogContent>
    </Dialog>
  )
}
